import { FIGURINHA_DESCRICAO_MAX, FIGURINHA_NOME_MAX } from "@foto/shared";

/** O que a tela edita numa imagem: o texto como está na caixa, ainda sem aparar. */
export interface Rascunho {
  nome: string;
  descricao: string;
}

/** O que o servidor guardou. */
export interface Guardado {
  nome: string;
  descricao: string | null;
}

export type ResultadoDaEdicao =
  | { tipo: "nada" }
  /** `campo` diz ao lado de qual caixa a frase aparece. */
  | { tipo: "erro"; campo: "nome" | "descricao"; mensagem: string }
  | { tipo: "salvar"; campos: { nome?: string; descricao?: string | null } };

/** O rascunho que a tela abre: a imagem como está guardada (sem descrição, a caixa vazia). */
export function rascunhoDe(item: Guardado): Rascunho {
  return { nome: item.nome, descricao: item.descricao ?? "" };
}

/**
 * Compara o rascunho com o que está guardado e diz o que enviar.
 *
 * Só viajam os campos que mudaram: mandar o nome de volta, igual, num pedido que só mexeu na descrição
 * apagaria sem querer um renome feito em outra aba. E descrição vazia vai como `null` — é assim que o
 * servidor entende "apague" (uma string vazia a deixaria, na busca, casando com tudo).
 *
 * Os limites são os do servidor, conferidos aqui para a recusa aparecer ao lado do campo e não depois
 * de uma ida à rede.
 */
export function pedidoDeEdicao(item: Guardado, rascunho: Rascunho): ResultadoDaEdicao {
  const nome = rascunho.nome.trim();
  const descricao = rascunho.descricao.trim();

  if (nome === "") return { tipo: "erro", campo: "nome", mensagem: "Dê um nome à imagem." };
  if (nome.length > FIGURINHA_NOME_MAX) {
    return {
      tipo: "erro",
      campo: "nome",
      mensagem: `O nome tem no máximo ${FIGURINHA_NOME_MAX} letras.`,
    };
  }
  if (descricao.length > FIGURINHA_DESCRICAO_MAX) {
    return {
      tipo: "erro",
      campo: "descricao",
      mensagem: `A descrição tem no máximo ${FIGURINHA_DESCRICAO_MAX} letras.`,
    };
  }

  const campos: { nome?: string; descricao?: string | null } = {};
  if (nome !== item.nome) campos.nome = nome;
  const novaDescricao = descricao === "" ? null : descricao;
  if (novaDescricao !== item.descricao) campos.descricao = novaDescricao;

  return Object.keys(campos).length === 0 ? { tipo: "nada" } : { tipo: "salvar", campos };
}
